import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import { getArticleBySlug, getArticlesByCategory, categories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <>
        <SeoHead title="文章未找到" description="该文章不存在或已被删除" canonicalUrl={`/article/${slug}`} />
        <Navbar />
        <main className="min-h-screen pt-24 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getArticlesByCategory(article.category)
    .filter((a) => a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <SeoHead
        title={`${article.title}——第一次的人妻`}
        description={article.excerpt}
        keywords={`第一次的人妻,${article.categoryName},${article.title},新婚指南,婚姻经营`}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
      />
      <Navbar />
      <main className="min-h-screen pt-20">
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500 mb-8">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.category}`} className="hover:text-primary-600 transition-colors">
              {article.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium truncate max-w-xs">{article.title}</span>
          </nav>

          <div className="mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              {article.categoryName}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.publishedAt}
              </span>
            </div>
          </div>

          <div className="w-full h-[400px] md:h-[500px] rounded-xl overflow-hidden mb-10">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose prose-lg max-w-none prose-headings:font-heading prose-headings:text-foreground-900 prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-h2:text-xl prose-h2:md:text-2xl prose-h2:mt-10 prose-h2:mb-5"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary-50 text-primary-700 text-sm font-medium hover:bg-primary-100 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回「{article.categoryName}」分类
            </Link>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full py-12 md:py-16 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900 mb-8">相关推荐</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group bg-white rounded-xl overflow-hidden border border-background-200 transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="w-full h-40 overflow-hidden">
                      <img
                        src={rel.coverImage}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-base text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2">{rel.author} · {rel.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: { '@type': 'Person', name: article.author },
            datePublished: article.publishedAt,
            image: article.coverImage,
            articleSection: article.categoryName,
            url: `https://diyiciderenqi.com/article/${article.slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://diyiciderenqi.com/' },
                { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://diyiciderenqi.com/category/${article.category}` },
                { '@type': 'ListItem', position: 3, name: article.title, item: `https://diyiciderenqi.com/article/${article.slug}` },
              ],
            },
          }),
        }}
      />
    </>
  );
}